import React from "react";
import { useLocale } from "../../i18n";
import LegalShell from "./LegalShell";

const P = ({ children }) => <p className="text-[var(--wr-ink)]">{children}</p>;
const UL = ({ children }) => <ul className="list-disc pl-6 space-y-2 marker:text-[var(--wr-red)]">{children}</ul>;

export default function PrivacyPolicy() {
  const { locale } = useLocale();
  const en = locale === "en";

  const sections = en ? [
    {
      heading: "In plain English",
      body: <>
        <P>WowRomania is a travel platform. We keep the amount of personal data we collect to the minimum we need to run the product and to make it useful to you. We do not sell personal data. We do not use dark patterns to trick you into sharing more.</P>
        <P>Privacy is not only a legal obligation for us — it is one of the principles on which WowRomania is built.</P>
        <P>This document explains exactly what we collect, why, how long we keep it, and the rights you have under EU GDPR and, where applicable, UK GDPR and CCPA.</P>
      </>,
    },
    {
      heading: "Who is responsible",
      body: <P>WowRomania is the data controller for the information collected through this platform. If you have any privacy or data protection questions, please contact us at <a href="mailto:contact.wowromania@gmail.com" className="underline decoration-[var(--wr-red)] underline-offset-4">contact.wowromania@gmail.com</a>.</P>,
    },
    {
      heading: "What we collect and why",
      body: <>
        <P><strong>AI Concierge chat.</strong> When you send a message to the concierge, we store your message and the AI's reply so we can display the conversation history and improve the product. Each conversation is tied to a random session ID generated in your browser — not to your identity.</P>
        <P><strong>Newsletter.</strong> Newsletter subscriptions are not open in Phase 1. When they open, we will store only your email address and your interface language, and only after your explicit consent.</P>
        <P><strong>Analytics.</strong> We use PostHog to understand how the product is used in aggregate — which pages people visit, which tools they use, how long they stay. This helps us fix bugs and improve tools. We do not link this data to personal identity.</P>
        <P><strong>Server logs.</strong> Our servers log basic request metadata (URL, timestamp, IP address, user-agent) for security and debugging. IPs are retained for a short window and then rotated out.</P>
        <P><strong>Locale + temperature-unit preferences</strong> are stored in your browser's localStorage. They never leave your device.</P>
      </>,
    },
    {
      heading: "What we do NOT collect",
      body: <UL>
        <li>We do not collect your name, phone number, address, date of birth, or payment details. At this time, we do not sell products or services directly through this website.</li>
        <li>We do not track you across the web with third-party ad cookies.</li>
        <li>We do not use fingerprinting techniques.</li>
      </UL>,
    },
    {
      heading: "Third-party services we rely on",
      body: <UL>
        <li><strong>OpenAI</strong> — Messages sent to the AI Concierge are processed through the OpenAI API solely to generate responses.</li>
        <li><strong>Open-Meteo</strong> — public weather data. No personal data is sent.</li>
        <li><strong>open.er-api.com</strong> — public currency data. No personal data is sent.</li>
        <li><strong>PostHog</strong> — product analytics (aggregate, anonymized).</li>
        <li><strong>Google Fonts</strong> — serves the typefaces used on the site.</li>
      </UL>,
    },
    {
      heading: "How long we keep data",
      body: <UL>
        <li>AI Concierge messages — 12 months from last session activity, then deleted.</li>
        <li>Server logs — 30 days, then rotated.</li>
        <li>Newsletter (when active) — until you unsubscribe or request deletion.</li>
      </UL>,
    },
    {
      heading: "Your rights",
      body: <>
        <P>Under GDPR you have the right to access, rectify, delete, restrict or export your personal data, and to object to certain uses. Write to <a href="mailto:contact.wowromania@gmail.com" className="underline decoration-[var(--wr-red)] underline-offset-4">contact.wowromania@gmail.com</a> and we will respond within 30 days.</P>
        <P>You also have the right to lodge a complaint with the Romanian supervisory authority (ANSPDCP).</P>
      </>,
    },
    {
      heading: "Children",
      body: <P>WowRomania is not directed at children under 16. We do not knowingly collect data from anyone under 16.</P>,
    },
    {
      heading: "Changes",
      body: <P>We will update this page if our practices change, and we will bump the "last updated" date at the top. Material changes will be announced on the homepage.</P>,
    },
  ] : [
    {
      heading: "Pe scurt",
      body: <>
        <P>WowRomania este o platformă de călătorii. Colectăm cât mai puține date personale — doar cât e strict necesar pentru a funcționa și a fi util. Nu vindem date personale. Nu folosim tertipuri pentru a te păcăli să împărtășești mai multe.</P>
        <P>Confidențialitatea nu este doar o obligație legală pentru noi, ci și unul dintre principiile după care construim WowRomania.</P>
        <P>Acest document explică exact ce colectăm, de ce, cât timp păstrăm și ce drepturi ai conform GDPR (UE și, unde se aplică, Marea Britanie) și CCPA.</P>
      </>,
    },
    {
      heading: "Cine e responsabil",
      body: <P>WowRomania este operatorul datelor colectate prin această platformă. Pentru orice întrebare privind confidențialitatea sau protecția datelor personale, ne poți contacta la <a href="mailto:contact.wowromania@gmail.com" className="underline decoration-[var(--wr-red)] underline-offset-4">contact.wowromania@gmail.com</a>.</P>,
    },
    {
      heading: "Ce colectăm și de ce",
      body: <>
        <P><strong>Chat cu Ghidul AI.</strong> Când trimiți un mesaj Ghidului, stocăm mesajul tău și răspunsul AI-ului pentru a afișa istoricul conversației și pentru a îmbunătăți produsul. Fiecare conversație e legată de un ID de sesiune aleator generat în browserul tău — nu de identitatea ta.</P>
        <P><strong>Newsletter.</strong> În Faza 1 înscrierile la newsletter nu sunt deschise. Când vor fi, vom stoca doar adresa de email și limba de interfață, doar după consimțământul tău explicit.</P>
        <P><strong>Analytics.</strong> Folosim PostHog pentru a înțelege în ansamblu cum este folosit produsul — ce pagini sunt vizitate, ce tools folosesc oamenii, cât stau. Ne ajută să reparăm bug-uri și să îmbunătățim tools. Aceste date nu sunt legate de identitate personală.</P>
        <P><strong>Log-uri de server.</strong> Serverele noastre înregistrează metadate de bază (URL, timestamp, IP, user-agent) pentru securitate și depanare. IP-urile sunt păstrate pentru o perioadă scurtă și apoi rotite.</P>
        <P><strong>Preferințele de limbă și unitate de temperatură</strong> sunt stocate în localStorage-ul browserului tău. Nu părăsesc niciodată dispozitivul tău.</P>
      </>,
    },
    {
      heading: "Ce NU colectăm",
      body: <UL>
        <li>Nu colectăm nume, telefon, adresă, dată de naștere sau detalii de plată. În prezent nu vindem produse sau servicii direct prin acest site.</li>
        <li>Nu te urmărim pe web cu cookie-uri publicitare terțe.</li>
        <li>Nu folosim tehnici de fingerprinting.</li>
      </UL>,
    },
    {
      heading: "Servicii terțe pe care le folosim",
      body: <UL>
        <li><strong>OpenAI</strong> — Mesajele trimise către Ghidul AI sunt procesate prin API-ul OpenAI exclusiv pentru generarea răspunsurilor.</li>
        <li><strong>Open-Meteo</strong> — date meteo publice. Nu se trimit date personale.</li>
        <li><strong>open.er-api.com</strong> — date valutare publice. Nu se trimit date personale.</li>
        <li><strong>PostHog</strong> — analytics de produs (agregate, anonimizate).</li>
        <li><strong>Google Fonts</strong> — servește fonturile folosite pe site.</li>
      </UL>,
    },
    {
      heading: "Cât timp păstrăm datele",
      body: <UL>
        <li>Mesaje Ghid AI — 12 luni de la ultima activitate a sesiunii, apoi șterse.</li>
        <li>Log-uri de server — 30 de zile, apoi rotite.</li>
        <li>Newsletter (când va fi activ) — până te dezabonezi sau ceri ștergerea.</li>
      </UL>,
    },
    {
      heading: "Drepturile tale",
      body: <>
        <P>Conform GDPR ai dreptul de acces, rectificare, ștergere, restricționare sau portabilitate a datelor personale și de a te opune anumitor prelucrări. Scrie la <a href="mailto:contact.wowromania@gmail.com" className="underline decoration-[var(--wr-red)] underline-offset-4">contact.wowromania@gmail.com</a> și îți vom răspunde în 30 de zile.</P>
        <P>Ai și dreptul de a depune o plângere la ANSPDCP (Autoritatea Națională de Supraveghere a Prelucrării Datelor cu Caracter Personal).</P>
      </>,
    },
    {
      heading: "Copii",
      body: <P>WowRomania nu se adresează copiilor sub 16 ani. Nu colectăm cu bună știință date de la persoane sub 16 ani.</P>,
    },
    {
      heading: "Modificări",
      body: <P>Vom actualiza această pagină dacă practicile noastre se schimbă și vom actualiza data de la început. Modificările importante vor fi anunțate pe pagina principală.</P>,
    },
  ];

  return (
    <LegalShell
      kicker={en ? "Trust · Privacy" : "Încredere · Confidențialitate"}
      title={en ? "Privacy Policy" : "Politica de confidențialitate"}
      updated={en ? "February 15, 2026" : "15 februarie 2026"}
      description={en
        ? "How WowRomania collects, uses and protects personal data. Written in plain language. GDPR-compliant."
        : "Cum colectează, folosește și protejează WowRomania datele personale. Scris pe înțelesul tuturor. Conform GDPR."}
      path="/privacy"
      sections={sections}
    />
  );
}
